import React, { useState } from 'react'
import { RadioTileGroup } from '@noissue-ui-kit/oldBranding/radioTile'
import { ContentfulNextJsImage } from '@components/common/ContentfulImage'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { IImage } from '@utils/contentful/contentfulImage'
import isEmpty from 'lodash/isEmpty'
import { IGenericBriefStepOption } from '@pageFeatures/design/briefingForm/types'

interface ITileSelectionOption {
  options: IGenericBriefStepOption[]
  defaultOption?: IGenericBriefStepOption | null
  onChange: (value) => void
}

const mapButtonTileOptionToRadioOption = (option: IGenericBriefStepOption) => ({
  displayLabel: option?.label,
  key: option?.value,
})

const getCustomColumnClassNames = (options) => {
  const optionCount = options?.length || 0
  // always display 3 items per row when having more than 5 items
  // and avoid to have n single odd item on the next row
  if (optionCount >= 5 || optionCount % 3 === 0) {
    return 'md:grid-cols-3'
  }
  return 'md:grid-cols-2'
}

function ImageDescription({ image, link }: { image: IImage; link?: string }) {
  const imageComponent = image?.imageUrl?.includes('ctfassets') ? (
    <ContentfulNextJsImage
      alt={image?.imageTitle}
      src={image?.imageUrl}
      objectFit="cover"
      width={140}
      height={96}
      quality={70}
      format="jpg"
    />
  ) : (
    <img src={image?.imageUrl} alt={image?.imageTitle} />
  )
  if (isEmpty(link)) {
    return imageComponent
  } else {
    return (
      <a href={link} target="_blank">
        {imageComponent}
      </a>
    )
  }
}

export function RadioTileWithImage({
  options,
  onChange,
  defaultOption = null,
}: ITileSelectionOption) {
  const [selectedOption, setSelectedOption] = useState(defaultOption)
  const radioGroupOptions = options?.map(mapButtonTileOptionToRadioOption)
  const hasImage = !!selectedOption?.image?.imageUrl

  const onSelectionChange = (event) => {
    const selectedOption = options.filter(
      (option) => option?.value === event.key
    )?.[0]
    setSelectedOption(selectedOption)
    onChange(selectedOption)
  }

  return (
    <div className="w-full font-mori">
      <RadioTileGroup
        options={radioGroupOptions}
        optionName={''}
        onChange={onSelectionChange}
        selected={mapButtonTileOptionToRadioOption(selectedOption)}
        radioGroupClassName={`grid-cols-1 ${getCustomColumnClassNames(
          options
        )}`}
      />

      {(selectedOption?.image?.imageUrl || selectedOption?.description) && (
        <div className="flex items-center justify-between flex-1 w-full p-6 mt-6 border-2 rounded-lg bg-core-white border-black-20 md:p-10 auto-rows-max place-content-stretch">
          {hasImage && (
            <div className="w-56 h-40 mr-6 rounded-lg overflow-hidden min-w-[140px]">
              <ImageDescription
                image={selectedOption?.image}
                link={selectedOption?.link}
              />
            </div>
          )}

          {selectedOption?.description && (
            <div className="">
              <ContentfulMarkdown
                overrideClass="text-lg leading-normal text-boulder"
                overrideStyles={{
                  a: ({ children, href }) => (
                    <a
                      className="text-xl pointer-events-auto text-pitaya"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {` ${children} `}
                    </a>
                  ),
                }}
              >
                {selectedOption?.description}
              </ContentfulMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
